// @flow

import React, { useEffect } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  GSGrid,
  Loader,
  View,
} from "@components";

import serviceActionCreators from "@modules/service/actionCreator";
import sharedActionCreators from "@modules/shared/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import type { ApprovableContainer } from "@modules/service/types";
import type { AbsenceMonitoring } from "@modules/service/reducer/absence-monitoring/types";
import type { State } from "@store/reducers";

type OwnProps = $Exact<{
  clientId: number,
  employeeId: number,
  onCancel: () => void,
  onSelect: (processId: number) => void,
}>;

type PropsFromState = $Exact<{
  absenceMonitoringList: Array<ApprovableContainer<AbsenceMonitoring>>,
  isLoading: boolean,
}>;

type PropsFromDispatch = $Exact<{
  getAbsenceMonitoringList: typeof serviceActionCreators.GetAbsenceMonitoringList,
  enterAbsenceMonitoringProcess: typeof sharedActionCreators.EnterAbsenceMonitoringProcess,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

type ValueAndScore = {
  value: any,
  isScoring: boolean,
};

type ValuesAndScoreRecord = {
  levels: ValueAndScore,
  noActionList: ValueAndScore,
  supportList: ValueAndScore,
  kpiTarget: ValueAndScore,
  hierarchyAreas: ValueAndScore,
};

function getValuesAndScore(row: Object): ValuesAndScoreRecord {
  const levels = row.content.levels.map(level => level.isEnabled).length;
  const noActionList = row.content.noActionReasons.map(reason => reason.isActive).length;
  const supportList = row.content.additionalSupportActions.map(action => action.isActive).length;
  const kpiTarget = row.content.kpis.at(-1)?.kpiMeasure;
  const hierarchyAreas = row.hierarchyAssignments.length;

  return {
    levels: { value: levels, isScoring: levels >= 3 },
    noActionList: { value: noActionList, isScoring: noActionList > 1 },
    supportList: { value: supportList, isScoring: supportList > 1 },
    kpiTarget: { value: kpiTarget, isScoring: kpiTarget <= 168 },
    hierarchyAreas: { value: hierarchyAreas, isScoring: hierarchyAreas > 0 },
  };
}

function getColorPillRender(value: number | string | boolean, isGreen: boolean) {
  return GSGrid.render.colorPill(value, isGreen && "mint", isGreen && "mint-d2");
}

export function SelectAbsenceMonitoringProcessModal(props: Props) {
  const { clientId, employeeId, onCancel, t } = props;

  useEffect(() => {
    props.getAbsenceMonitoringList({ clientId, isActive: true });
  }, [clientId]);

  function handleSelect(processId: number) {
    props.enterAbsenceMonitoringProcess({ clientId, employeeId, processId });
    props.onSelect(processId);
  }

  const rows = props.absenceMonitoringList || [];

  const columns = [
    {
      name: t("ID"),
      align: "center",
      width: 60,
      key: "id",
    },
    {
      name: t("ABSENCE_MONITORING_PROCESS_NAME"),
      key: "name",
      align: "left",
      renderCell: ({ row }: Object) => GSGrid.render.ellipsis(row.content.name),
    },
    {
      key: "levels",
      name: t("LEVELS"),
      renderCell: ({ row }: { row: Object }) => {
        const levels = getValuesAndScore(row).levels;
        return getColorPillRender(levels.value, levels.isScoring);
      },
    },
    {
      key: "noActionList",
      name: t("NO_ACTION_LIST"),
      renderCell: ({ row }: { row: Object }) => {
        const noActionList = getValuesAndScore(row).noActionList;
        return getColorPillRender(noActionList.value, noActionList.isScoring);
      },
    },
    {
      key: "supportList",
      name: t("SUPPORT_LIST"),
      renderCell: ({ row }: { row: Object }) => {
        const supportList = getValuesAndScore(row).supportList;
        return getColorPillRender(supportList.value, supportList.isScoring);
      },
    },
    {
      key: "kpiTarget",
      name: t("KPI_TARGET"),
      renderCell: ({ row }: { row: Object }) => {
        const kpiTarget = getValuesAndScore(row).kpiTarget;
        return getColorPillRender(kpiTarget.value, kpiTarget.isScoring);
      },
    },
    {
      key: "hierarchyAreas",
      name: t("HIERARCHY_AREAS"),
      renderCell: ({ row }: { row: Object }) => {
        const hierarchyAreas = getValuesAndScore(row).hierarchyAreas;
        return getColorPillRender(hierarchyAreas.value, hierarchyAreas.isScoring);
      },
    },
    {
      key: "rating",
      name: t("RATING"),
      renderCell: ({ row }: { row: Object }) => {
        const valuesAndScore = Object.values(getValuesAndScore(row));
        if (!Array.isArray(valuesAndScore)) return;
        // $FlowIgnore
        const totalScore = valuesAndScore.filter(v => v.isScoring === true).length;
        return GSGrid.render.scorePill(totalScore);
      },
    },
    {
      name: t("STATUS"),
      width: 105,
      renderCell: ({ row }: Object) =>
        GSGrid.render.statusPill(t(row.content.isActive ? "ACTIVE" : "INACTIVE")),
    },
    {
      name: t("SELECT"),
      width: 60,
      onCellClick: row => handleSelect(row.id),
      renderCell: () => GSGrid.render.materialIcon("check_circle"),
    },
  ];

  return (
    <Modal className="modal-large">
      <ModalHeader toggle={onCancel} className="d-flex justify-content-between">
        {t("SELECT_ABSENCE_MONITORING_PROCESS")}
      </ModalHeader>
      <ModalBody>
        <View padding="1rem">
          {props.isLoading && <Loader inline />}
          {rows.length ? (
            <GSGrid columns={columns} rows={rows} />
          ) : (
            <View justifyContent="center" margin={[3, 0, 3, 0]}>
              <h4 className="text-pinkish-grey">{t("ABSENCE_MONITORING_LIVE_REASONS")}</h4>
            </View>
          )}
        </View>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onCancel}>
          {t("CANCEL")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

const mapStateToProps = (state: State): PropsFromState => ({
  absenceMonitoringList: serviceSelectors.getAbsenceMonitoringList(state),
  isLoading: serviceSelectors.isLoading(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getAbsenceMonitoringList: serviceActionCreators.GetAbsenceMonitoringList,
  enterAbsenceMonitoringProcess: sharedActionCreators.EnterAbsenceMonitoringProcess,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  localize(),
)(SelectAbsenceMonitoringProcessModal);
